import React, { FC, useMemo } from 'react';
import { AppName } from '../../../apps/definition';
import { StaticAnswerType } from '../../../models/QAmodels';
import { SupportedLlm } from '../../../models/User';
import { useQAController } from '../../../scripts/QAController';
import { useFlag, useUserSafe } from '../../../scripts/hooks';
import { useRecommendedPrompts } from '../../../scripts/hooks/prompts';
import { RecommendedWorkflows } from '../../recommendedWorkflows/RecommendedWorkflows';
import { QATextInputBox, QATextInputProps } from './QATextInputBox';

export interface SendQueryArgs {
  queryText: string;
  conversation_id?: string;
  filter_skills: FilterSkills[];
  llm_preference: SupportedLlm | null;
  isBaseLLM: boolean;
  continueTopic?: boolean;
  bot_id?: string;
  custom_instructions?: string;
}

export interface ConnectedApps {
  displayName: string;
  appName: AppName;
  id?: number;
  workspaceName?: string;
  connectionName?: string;
}

export interface FilterSkills {
  app_name: AppName;
  app_id: number;
}

export const QATextInputView: FC<QATextInputProps> = ({
  className,
  autofocus,
  hideNewTopicButton,
  hideStopAnswerGenerationButton,
  showPopupFromBottom,
  continueConversationId,
  setOpenPaywallModal,
  sendQueryCallback,
}: QATextInputProps) => {
  const aiControlsEnabled = useFlag('aiControlsEnabled');
  const isWorkflowsEnabled = useFlag('workflowsEnabled');

  const user = useUserSafe();
  const { prompts: recommendedPrompts } = useRecommendedPrompts();

  const qaController = useQAController();
  const messages = qaController.useMessages();
  const { isNewTopic } = qaController.getIsNewTopic();

  const isOnboardingMessage = useMemo(
    () =>
      messages[messages.length - 1]?.extraData?.staticAnswerType ===
        // eslint-disable-next-line @cspell/spellchecker
        StaticAnswerType.ONBOARDING_INITAL ||
      messages[messages.length - 1]?.extraData?.staticAnswerType ===
        StaticAnswerType.ONBOARDING_ASK_QUESTION,
    [messages]
  );

  const showRecommendedWorkflows = useMemo(() => {
    return (
      isWorkflowsEnabled &&
      !isOnboardingMessage &&
      isNewTopic &&
      !continueConversationId
    );
  }, [
    continueConversationId,
    isWorkflowsEnabled,
    isNewTopic,
    isOnboardingMessage,
  ]);

  return (
    <div className="flex flex-col items-center my-2 w-screen sm:w-full px-1 sm:px-0">
      {showRecommendedWorkflows && (
        <div className="w-full max-w-[750px] mb-2">
          <RecommendedWorkflows recommendedWorkflows={recommendedPrompts} />
        </div>
      )}
      <QATextInputBox
        autofocus={autofocus}
        className={className}
        continueConversationId={continueConversationId}
        hideNewTopicButton={hideNewTopicButton}
        hideStopAnswerGenerationButton={hideStopAnswerGenerationButton}
        sendQueryCallback={sendQueryCallback}
        setOpenPaywallModal={setOpenPaywallModal}
        showPopupFromBottom={showPopupFromBottom}
      />
      {aiControlsEnabled && (
        <div className="mt-2 max-w-[750px] text-center text-xs text-cloud-40">
          {user.orgByOrgId.disclaimer}
        </div>
      )}
    </div>
  );
};
